import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";

// Serializer
import OperatingOrderInterfaceSerializer from "issara-sdk/types/OperatingOrderSerializer_apps_ORM";
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";

// API
import OperatingOrderList from "issara-sdk/apis/OperatingOrderList_apps_ORM";
import OperatingOrderTriggerView from "issara-sdk/apis/OperatingOrderTriggerView_apps_INF";
import UserList from "issara-sdk/apis/UserList_users";
import EncounterDetail from "issara-sdk/apis/EncounterDetail_core";

export type Props = {

}

export type State = Partial<{
  // seq
  OperatingOrderInterfaceSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    OperatingOrderInterfaceList: OperatingOrderInterfaceSerializer[];
    selectedItems: any[];
    userOptions: any[];
    encounterList: EncounterSerializer[];
    searchOperatingOrder: FilterSearchType;
    paginatedItems: PaginatedItemsType;
  }> | null;
}>;

export type FilterSearchType = Partial<{
  hn: string | null;
  encounterNumber: string | null;
  code: string | null;
}>;

type PaginatedItemsType = Partial<{
  items: OperatingOrderInterfaceSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

type PickedState = Partial<Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage">>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "OperatingOrderInterface";
  restart?: boolean;
  clear?: boolean;
};

export type SaveType = "TRIGGER";

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

export type AccessorType = Partial<Record<keyof OperatingOrderInterfaceSerializer, string>>;

export type WidthsType = Partial<Record<keyof OperatingOrderInterfaceSerializer, number>>;

// Handle Action
type ActionType =
  | { action: "SEARCH"; cardKey: string; page?: number; pageSize?: number }
  | { action: "CLEAR"; cardKey: string }
  | { action: "SAVE"; cardKey: string; type: SaveType; selectedItems: any[] };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  OperatingOrderInterfaceSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  errorMessage: null,
  successMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardOperatingOrderInterface";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR: "CLEAR",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const FILTER_INIT: FilterSearchType = {
  hn: "",
  encounterNumber: "",
  code: "",
};

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

// * Hardcode
export const PaymentStatusOptions = [
  { key: 1, value: 1, text: "PENDING" },
  { key: 2, value: 2, text: "READY" },
  { key: 3, value: 3, text: "PAID" },
  { key: 4, value: 4, text: "" } //*
];

// * Based on Backed
export const TypeOptions = [
  { key: 1, value: "EL", text: "Elective Case (นัดหมายสำหรับ Encounter ใหม่)" },
  { key: 2, value: "ER", text: "Emergency Case หรือ ทำใน Encounter เดิม" },
  { key: 3, value: "N", text: "Non-Schedule" },
  { key: 4, value: "EL_NOT_APP", text: "Elective Case หรือ ทำใน Encounter เดิม" },
];

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      OperatingOrderInterfaceSequence: {
        ...state.OperatingOrderInterfaceSequence,
        sequenceIndex: "Action",
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    },
    // * issue 75070
    // () => Action(controller, { ...params, action: "SEARCH", cardKey: CARD_NAME })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchData,
    [ACTIONS.CLEAR]: HandleClearData,
    [ACTIONS.SAVE]: HandleSaveData,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandleSearchData: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const searchOperatingOrder = state.OperatingOrderInterfaceSequence?.searchOperatingOrder;
  const paginatedItems = state.OperatingOrderInterfaceSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  // * issue 75098
  if (!searchOperatingOrder?.hn) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: "กรุณาระบุ HN ก่อนทำรายการ",
      },
    });

    return;
  }

  let data = {
    limit: pageSize,
    offset,
    emr__encounter__patient__hn: searchOperatingOrder?.hn, // * issue 75080
    encounter_number: searchOperatingOrder?.encounterNumber,
    code: searchOperatingOrder?.code,
  };

  const result = await GetOperatingOrderInterfaceData(controller, data);

  if (result?.items) {
    // const uniqueData = (result.items || [])
    //   .filter(
    //     (item: OperatingOrderInterfaceSerializer, index: number, self: any) =>
    //       index === self.findIndex((t: any) => t.edited_doctor === item.edited_doctor)
    //   )
    //   .map((item: OperatingOrderInterfaceSerializer) => item?.edited_doctor);

    // const userOptions = await Promise.all(
    //   uniqueData.map(async (id: number) => {
    //     const user = await GetUserList(controller, id);

    //     return user;
    //   })
    // );

    const uniqueEncounter = (result.items || [])
      .filter(
        (item: OperatingOrderInterfaceSerializer, index: number, self: any) =>
          index === self.findIndex((t: OperatingOrderInterfaceSerializer) => t.encounter === item.encounter)
      )
      .map((item: OperatingOrderInterfaceSerializer) => item?.encounter);

    const encounterList = await Promise.all(
      uniqueEncounter.map(async (id: number) => await GetEncounterDetail(controller, { id }))
    );

    controller.setState({
      OperatingOrderInterfaceSequence: {
        ...state.OperatingOrderInterfaceSequence,
        OperatingOrderInterfaceList: result?.items,
        // userOptions: userOptions,
        encounterList,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

const HandleSaveData: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${CARD_NAME}_${params.type}`]: "LOADING",
    },
  });

  const [saveResp, saveErr] = await SaveOperatingOrderInterface(controller, {
    type: params.type,
    selectedItems: params.selectedItems,
  });

  //! Response ตอน Trigger แล้วได้ "" ทำให้ Skip Condition เลยแสดง Button Status ไม่ถูก
  if (saveResp || saveResp === "") {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: "The system has successfully",
      },
    });

    HandleSearchData(controller, { action: "SEARCH", cardKey: CARD_NAME });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: saveErr,
      },
    });
  }
};

const HandleClearData: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    OperatingOrderInterfaceSequence: {
      ...state.OperatingOrderInterfaceSequence,
      searchOperatingOrder: { ...FILTER_INIT },
      OperatingOrderInterfaceList: [],
      encounterList: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    },
  });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetOperatingOrderInterfaceData: Handler = async (controller, params) => {
  const state = controller.getState();

  /* params: 'hn', 'encounter_number', 'code' */
  const [resp, err] = await OperatingOrderList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GetUserList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await UserList.list({
    apiToken: controller.apiToken,
    params: {
      pk: params.id,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items?.[0];
};

const GetEncounterDetail: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await EncounterDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.id,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const SaveOperatingOrderInterface: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await OperatingOrderTriggerView.put({
    apiToken: controller.apiToken,
    data: {
      operating_orders: params?.selectedItems || [],
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

/**========================================================================
 **                               Utils
 *========================================================================**/

export const accessorList: Partial<Record<keyof OperatingOrderInterfaceSerializer, string>> = {
  // * Based on Backed Table
  code: "Code",
  emr: "EMR",
  type: "Type",
  order_status: "Order Status",
  order_payment_status: "Order Payment Status",
  start_date: "Start Date",
  start_time: "Start Time",
  end_date_time: "End Date Time",
  status_name: "Status Name",
  // specific_type: "Specific Type",
  // id: "ID",
  // edited_doctor: "Edited Doctor",
  // doctor_edit_time: "Doctor Edit Time",
  // order_type: "Order Type",
  // order_time: "Order Time",
  // order_by: "Order By",
  // order_ack_div: "Order Div",
  // order_receive_by: "Order Receive By",
  // order_receive_time: "Order Receive Time",
  // order_ack_by: "Order Ack By",
  // order_ack_time: "Order Ack Time",
  // order_perform_by: "Order Perform By",
  // order_perform_time: "Order Perform Time",
  // order_perform_div: "Order Perform Div",
  // out_perform_div: "Out Perform Div",
  // order_approve_time: "Order Approve Time",
  // clinic_type: "Clinic",
  // action: "Action",
  // is_appointment: "Is Appointment",
  // operating_room: "Operating Room",
  // blood_order_item: "Blood Order Item",
  // contrast_media: "Contrast Media",
  // is_med_block: "Is Med Block",
  // med_block_date: "Med Block Date",
  // is_anesthesia: "Is Anesthesia",
  // anesthesia_date: "Anesthesia Date",
  // is_other: "Is Other",
  // other: "Other",
  // other_date: "Other Date",
  // main_team: "Main Team",
  // reason: "Reason",
  // admit_order: "Admit Order",
  // hn: "HN",
  // en: "EN",
  // // patient_id: "Patient ID",
  // patient_name: "Patient Name",
  // patient_age: "Patient Age",
  // patient_birthdate: "Patient Birthdate",
  // patient_tel: "Patient Tel",
  // is_ipd_patient: "Is IPD Patient",
  // division_name: "Division Name",
  // division_code: "Division Code",
  // operating_detail: "Operating Detail",
  // coverage: "Coverage",
  // // location: "Location",
  // location_name: "Location",
  // gender: "Gender",
  // implant: "Implant",
  // operating_room_no: "Operating Room No",
  // primary_doctor_name: "Primary Doctor Name",
  // assistant_primary_doctor_name: "Assistant Primary Doctor Name",
  // // readonly: boolean,
  // appointment_id: "Appointment ID",
  // // anesthesia_method: "Anesthesia Method",
  // anesthesia_method_name: "Anesthesia Method",
  // preoperative_diagnosis_summary: "Preoperative Diagnosis Summary",
  // pre_operation_summary: "Pre Operation Summary",
  // pre_operation_product_summary: "Pre Operation Product Summary",
  // other_treatment_summary: "Other Treatment Summary",
  // position_summary: "Position Summary",
  // is_paid: "Is Paid",
  // is_encounter_expired: "Is Encounter Expired",
  // is_checkin: "Is Checkin",
  // nurse_status: "Nurse Status",
  // blood_request_summary: "Blood Request Summary",
  // // status_label: "Status Label",
  // estimate_operation_hour: "Estimate Operation Hour",
  // estimate_operation_minute: "Estimate Operation Minute",
  // is_advance: "Is Advance",
  // is_out_perform_div: "Is Out Perform Div",
  // is_seen: "Is Seen",
  // created: "Created",
  // edited: "Edited",
  // // status: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 10 | 11 | 12 | 13,
  // checkin_code: "Checkin Code",
  // procedure_type: "Procedure Type",
  // frozen_section: "Frozen Section",
  // mammogram: "Mammogram",
  // estimate_operation_time: "Estimate Operation Time",
  // total_estimate_operation_time: "Total Estimate Operation Time",
  // remark: "Remark",
  // progression_cycle: "Progression Cycle",
  // // encounter: "Encounter",
  // edit_user: "Edit User",
  // asa_class: "ASA Class",
};

export const GenerateTableHeader = (
  columnnWidths?: WidthsType
): TableHeaderType<OperatingOrderInterfaceSerializer>[] => {
  return (Object.entries(accessorList) as [keyof OperatingOrderInterfaceSerializer, string][]).map(
    ([key, label]) => ({
      Header: label,
      accessor: key,
      ...(columnnWidths && columnnWidths[key] ? { width: columnnWidths[key] } : {}),
    })
  );
};